import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { Toro } from '../../api/common/enumerations';
import { FirmwareManagerService } from '../../api/managers/firmware-manager.service';
import { LocationManagerService } from '../../api/managers/location-manager.service';
import { FirmwareUpgradeProgressNotification } from '../../api/models/notifications/firmware-upgrade-progress-notification.model';
import { Constants } from '../../core/constants';
import { BaseIrrigation } from '../../irrigation/base-irrigation';
import { MessageBoxService } from '../../services/message-box.service';
import { OverlayManagerService } from '../../services/overlay-manager.service';
import { MessageBoxButton, MessageBoxType } from '../message-box/message-box.component';
import { NotificationManagerService } from '../../api/managers/notification-manager.service';
import { getCurrentLocationDetail } from '../../accounts/store/toro-location/toro-location.selectors';
import { ToroLocationDetailed } from '../../api/models/toro-location-detailed.model';
import { Store } from '@ngrx/store';
import { ToroLocationState } from '../../accounts/store/toro-location/toro-location.entity';
import { LocalCacheManager } from '../../api/managers/local-cache.manager';

@Component({
    selector: 'app-firmware-upgrade-progress',
    templateUrl: './firmware-upgrade-progress.component.html',
    styleUrls: ['./firmware-upgrade-progress.component.less']
})
export class FirmwareUpgradeProgressComponent extends BaseIrrigation implements OnInit, OnDestroy {
    isUpgradeComplete = false;

    private hasPercentCompleteReached100Percent = false;
    private finalizingTimerRef;
    private lastResortTimerRef;
    private finishingUpMessage: string;
    magnetoFwUpdatePercentCompleteSubscription: Subscription;
    getGatewayNetworkConfigurationSubscription: Subscription;
    accountId: number;
    locSubscription: Subscription;
    firmwareUpgradedSubscription: Subscription;
    private refreshTimerRef;
    // =========================================================================================================================================================
    // C'tor, Init and Destroy
    // =========================================================================================================================================================

    constructor(public firmwareManager: FirmwareManagerService,
                private messageBoxService: MessageBoxService,
                private locationManager: LocationManagerService,
                private overlayManager: OverlayManagerService,
                private router: Router,
                private localCacheManager: LocalCacheManager,
                private locationStore: Store<ToroLocationState>,
                private notificationManager: NotificationManagerService)
    {
        super(router);
    }

    ngOnInit() {
        this.firmwareUpgradedSubscription = this.firmwareManager.firmwareUpgraded
            .pipe(untilDestroyed(this))
            .subscribe((message: string) => {
                this.clearTimers();
                this.overlayManager.hideProcessingOverlay.next();
                this.messageBoxService.displayMessageBox(message, MessageBoxType.Success,
                    () => { this.firmwareUpgradedCallback(message) },
                    () => { this.firmwareUpgradedCallback(message) });
            });

        this.firmwareManager.noFirmwareUpgradeReceived
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.messageBoxService.displayMessageBox('Unable to update', MessageBoxType.Info, () => {
                    this.goToDash(false);
                });
            });

        this.firmwareManager.evoRequiresUsbFlashDriveUpdate
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.goToDash(false);
            });

        this.magnetoFwUpdatePercentCompleteSubscription = this.firmwareManager.fwUpdatePercentCompleteChange
            .pipe(untilDestroyed(this))
            .subscribe((notification: FirmwareUpgradeProgressNotification) => {
                if (notification.PercentComplete < 100 && this.hasPercentCompleteReached100Percent) {
                    this.hasPercentCompleteReached100Percent = false;
                }

                if (notification.PercentComplete >= 100 && !this.hasPercentCompleteReached100Percent) {
                    this.hasPercentCompleteReached100Percent = true;

                    // If the notification is for a Gateway update, bail out.
                    if (notification.DeviceType === Toro.DeviceTypeId.Gateway) return;

                    if (notification.DeviceType === Toro.DeviceTypeId.MAGNETO || notification.DeviceType === Toro.DeviceTypeId.LXCSC) {
                        this.finishingUpMessage = Constants.Strings.FinalizingSomeTime;
                        this.finalizingTimerRef = setTimeout(() => {
                            // We should have received a firmware complete message by now, but just in case, we will give the process two more minutes.
                            // We won't receive the expected message when working with older versions of CLMR/CLM1 FW. This is a total hack, but we have no option.
                            this.finishingUpMessage = Constants.Strings.FinishingUp;
                            this.lastResortTimerRef = setTimeout(() => {
                                this.assumeFirmwareUpgradeHasCompleted();
                            }, 30 * 1000);

                        }, 20 * 1000);
                    } else {
                        this.finishingUpMessage = Constants.Strings.FinalizingSomeTime;
                        this.finalizingTimerRef = setTimeout(() => {
                            // We should have received a firmware complete message by now, but just in case, we will give the process two more minutes.
                            // We won't receive the expected message when working with older versions of CLMR/CLM1 FW. This is a total hack, but we have no option.
                            this.finishingUpMessage = 'Finishing up...';
                            this.lastResortTimerRef = setTimeout(() => {
                                this.assumeFirmwareUpgradeHasCompleted();
                            }, Constants.OneMinuteInMs);

                        }, Constants.TwoMinutesInMs);
                    }

                }
            });

        this.firmwareManager.installingFirmwareOnDevice
            .pipe(untilDestroyed(this))
            .subscribe(() => this.clearTimers());

        this.firmwareManager.remappingRecords
            .pipe(untilDestroyed(this))
            .subscribe(() => this.clearTimers());

        this.setLocSubscription();
    }

    setLocSubscription() {
        this.locSubscription =
            this.locationStore.select(getCurrentLocationDetail)
                .subscribe((location: ToroLocationDetailed) => {
                    if (location && location !== null) {
                        this.accountId = location.AccountId;
                    }
                    // else {
                    //     this.router.navigate(['/dashboard/locations']);
                    // }
                });
    }

    firmwareUpgradedCallback(message) {
        // this.firmwareUpgradedSubscription.unsubscribe();
        if (message.includes('Firmware') || this.locationManager.currentLocation === null) {
            this.goToLocationsPage(false);
            return;
        }
        this.goToDash(false);
    }

    ngOnDestroy() {
        /**  Required for untilDestroyed() */

        this.clearTimers();

        // if(this.magnetoFwUpdatePercentCompleteSubscription){
        //     this.magnetoFwUpdatePercentCompleteSubscription.unsubscribe();
        // }
        if (this.getGatewayNetworkConfigurationSubscription) {
            this.getGatewayNetworkConfigurationSubscription.unsubscribe();
        }

        if (this.locSubscription) {
            this.locSubscription.unsubscribe();
        }

        if (this.firmwareUpgradedSubscription) {
            this.firmwareUpgradedSubscription.unsubscribe();
        }
    }

    // =========================================================================================================================================================
    // Event Handlers
    // =========================================================================================================================================================

    showCancelFirmwareUpgradeConfirmation() {
        const deviceId = this.firmwareManager.firmwareUpgradeProgressNotification.DeviceId;
        const deviceTypeId = this.firmwareManager.firmwareUpgradeProgressNotification.DeviceType;

        this.messageBoxService.displayMessageBox(
            Constants.Strings.CancelDeviceFwUg,
            MessageBoxType.Warning,
            this.cancelFirmwareUpgradeConfirmationCallback.bind(this, deviceId, deviceTypeId)
        );
    }

    onDismissDialog() {
        //this.navigateAfterCompletion();
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    private clearTimers() {
        if (this.finalizingTimerRef) clearTimeout(this.finalizingTimerRef);
        if (this.lastResortTimerRef) clearTimeout(this.lastResortTimerRef);
        if (this.refreshTimerRef) clearTimeout(this.refreshTimerRef);
    }

    private assumeFirmwareUpgradeHasCompleted() {
        this.clearTimers();
        this.firmwareManager.manuallyResetFirmwareUpgradeState();
        this.hasPercentCompleteReached100Percent = false;
        this.messageBoxService.displayMessageBox(Constants.Strings.FwUgComplete, MessageBoxType.Success,
            () => { this.upgradeCompleteOkCloseCallback() },
            () => { this.upgradeCompleteOkCloseCallback() });
    }

    upgradeCompleteOkCloseCallback() {
        //this.notificationManager.startPollingByInterval(this.accountId, Constants.longPollInterval);
        this.firmwareManager.isUpgradingFirmware = false;
        window.location.reload();
    }


    private cancelFirmwareUpgradeConfirmationCallback(deviceId: number, deviceTypeId: number, button) {
        if (button === MessageBoxButton.Yes) {
            this.cancelFirmwareUpgrade(deviceId, deviceTypeId);
        }
    }

    private cancelFirmwareUpgrade(deviceId: number, deviceTypeId: number) {
        this.overlayManager.showProcessingOverlay.next(Constants.Strings.Canceling);

        this.firmwareManager.cancelFirmwareUpgrade(deviceId, deviceTypeId)
            .pipe(
                finalize(() => {
                    //this.navigateAfterCompletion();
                })
            )
            .subscribe(() => {
                this.overlayManager.hideProcessingOverlay.next();
                this.messageBoxService.displayMessageBox(Constants.Strings.FwUgCancelled, MessageBoxType.Success,
                    () => { this.cancelUpgradeCallback(deviceTypeId) },
                    () => { this.cancelUpgradeCallback(deviceTypeId) });
            }, err => {
                this.overlayManager.hideProcessingOverlay.next();
                this.messageBoxService.displayMessageBox(err['error']['Message'], MessageBoxType.Error);
            });
    }

    cancelUpgradeCallback(deviceTypeId) {
        this.firmwareManager.isUpgradingFirmware = false;
        this.firmwareManager.firmwareUpgradeCancelled.next();
        if (deviceTypeId === Toro.DeviceTypeId.Gateway) {
            //clear timer if gateway firmware upgrade exit clicked
            this.firmwareManager.clearGatewayFirmwareProgressTimer();
        } else if (deviceTypeId === Toro.DeviceTypeId.MAGNETO) {
            //clear timer if magneto firmware upgrade exit clicked
            this.firmwareManager.clearMagnetoFirmwareProgressTimer();
        } else if (deviceTypeId === Toro.DeviceTypeId.LXCSC) {
            //clear timer if LXCSC firmware upgrade exit clicked
            this.firmwareManager.clearLXCFirmwareProgressTimer();
        }
    }

    private goToLocationsPage(useCache: boolean = true) {
        this.router.navigate(['/dashboard/locations'], { queryParams: { useCache: useCache } });
    }

    private goToDash(useCache: boolean = true) {
        if (window.location.toString().includes('irrigation/main')) {
            this.goToDashboard(this.locationManager.currentLocation.Id, useCache);
            this.refreshTimerRef = setTimeout(() => {
                this.firmwareManager.reloadData.next();
            }, 10);
            return;
        }

        this.goToDashboard(this.locationManager.currentLocation.Id, useCache);
    }

    private navigateAfterCompletion() {
        if (this.router.url.includes('/controllers/add')) {
            this.goToControllersTab(this.locationManager.currentLocation.Id, false);
        }
        else {
            this.goToDash(false);
        }
    }
}
